import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Zap,
  Info,
  AlertCircle,
  CheckCircle,
  XCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
  Target,
  Crosshair,
  Activity,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const SpeedReactor = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game settings
  const [gameMode, setGameMode] = useState("intro"); // intro, tutorial, play, results
  const [difficulty, setDifficulty] = useState("standard"); // easy, standard, advanced
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);

  // Game state
  const [isActive, setIsActive] = useState(false);
  const [stimulusType, setStimulusType] = useState("color"); // color, shape, position
  const [reactionTimes, setReactionTimes] = useState([]);
  const [falseStarts, setFalseStarts] = useState(0);
  const [missedStimuli, setMissedStimuli] = useState(0);
  const [roundsCompleted, setRoundsCompleted] = useState(0);
  const [totalRounds, setTotalRounds] = useState(20);
  const [currentStimulus, setCurrentStimulus] = useState(null);
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);
  const [anticipatoryTime, setAnticipatoryTime] = useState(0);

  // Game metrics
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);
  const [stimulusStartTime, setStimulusStartTime] = useState(null);
  const [stimulusEndTime, setStimulusEndTime] = useState(null);
  const [avgReactionTime, setAvgReactionTime] = useState(0);
  const [bestReactionTime, setBestReactionTime] = useState(0);
  const [worstReactionTime, setWorstReactionTime] = useState(0);
  const [gameWasCompleted, setGameWasCompleted] = useState(false);

  // Timer settings
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes game time
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const stimulusTimerRef = useRef(null);
  const timeoutRef = useRef(null);
  const gameActive = useRef(false);

  // Visual settings
  const reactionAreaRef = useRef(null);
  const lastInteractionTime = useRef(Date.now());

  // Colors based on processing speed category
  const primaryColor = "from-orange-500 to-red-600";
  const primaryColorSolid = "bg-orange-500";
  const secondaryColor = "text-orange-300";
  const tertiaryColor = "bg-orange-500/10";

  // Tutorial state
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialActive, setTutorialActive] = useState(false);

  // Difficulty settings
  const difficultySettings = {
    easy: {
      minDelay: 2000, // min milliseconds before stimulus appears
      maxDelay: 5000, // max milliseconds before stimulus appears
      stimulusDuration: 2000, // how long stimulus stays visible
      variableStimuli: false, // only one type of stimulus
    },
    standard: {
      minDelay: 1500,
      maxDelay: 4000,
      stimulusDuration: 1500,
      variableStimuli: true, // different types of stimuli
    },
    advanced: {
      minDelay: 1000,
      maxDelay: 3000,
      stimulusDuration: 1000,
      variableStimuli: true,
      distractors: true, // includes distractor stimuli
    },
  };

  // Stimuli definitions
  const stimuli = {
    color: [
      "bg-green-500",
      "bg-blue-500",
      "bg-purple-500",
      "bg-yellow-500",
      "bg-red-500",
    ],
    shape: ["circle", "square", "triangle", "diamond", "hexagon"],
    position: ["top", "right", "bottom", "left", "center"],
  };

  // Fetch high scores when component mounts
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // Reference the single leaderboard document for the "reaction-time" game
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "reaction-time"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Retrieve scores for the current difficulty (key is the difficulty as a string)
          const scores =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scores);
        } else {
          // If the document doesn't exist, initialize with an empty array
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, difficulty]);

  // Start timer when game starts
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Format milliseconds
  const formatMs = (ms) => {
    return `${ms} ms`;
  };

  // Start a new game
  const startGame = () => {
    console.log("Starting game...");

    // Reset all game state
    setScore(0);
    setReactionTimes([]);
    setFalseStarts(0);
    setMissedStimuli(0);
    setRoundsCompleted(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setAvgReactionTime(0);
    setBestReactionTime(0);
    setWorstReactionTime(0);
    setTimeLeft(180);

    // Set playing state
    setIsPlaying(true);
    gameActive.current = true;

    setGameStartTime(Date.now());
    setGameEndTime(null);

    // Start first round
    startNewRound();
  };

  const endGame = () => {
    console.log("Ending game...");
  
    clearTimeout(timeoutRef.current);
    clearInterval(timerRef.current);
    clearTimeout(stimulusTimerRef.current);
  
    setIsPlaying(false);
    gameActive.current = false;
    setIsActive(false);
    setGameEndTime(Date.now());
    setGameMode("results");
  
    // ✅ Only mark as completed if all trials were played
    const gameWasFullyCompleted = currentTrialIndex + 1 >= totalTrials; // <-- adjust variable name if needed
    setGameWasCompleted(gameWasFullyCompleted);
  
    if (reactionTimes.length > 0) {
      const sum = reactionTimes.reduce((a, b) => a + b, 0);
      const avg = Math.round(sum / reactionTimes.length);
      const best = Math.min(...reactionTimes);
      const worst = Math.max(...reactionTimes);
  
      setAvgReactionTime(avg);
      setBestReactionTime(best);
      setWorstReactionTime(worst);
    }
  };
  

  // Start a new round
  const startNewRound = () => {
    if (!gameActive.current) return;

    setIsActive(false);
    setCurrentStimulus(null);

    // Determine delay for this round
    const settings = difficultySettings[difficulty];
    const delay = Math.floor(
      Math.random() * (settings.maxDelay - settings.minDelay) +
        settings.minDelay
    );

    // Set a timeout for showing the stimulus
    timeoutRef.current = setTimeout(() => {
      if (!gameActive.current) return;

      // Choose stimulus type if variable
      let type = "color"; // Default
      if (settings.variableStimuli) {
        const types = Object.keys(stimuli);
        type = types[Math.floor(Math.random() * types.length)];
      }

      // Choose a specific stimulus
      const possibleStimuli = stimuli[type];
      const stimulus =
        possibleStimuli[Math.floor(Math.random() * possibleStimuli.length)];

      // Set the active stimulus
      setStimulusType(type);
      setCurrentStimulus(stimulus);
      setIsActive(true);
      setStimulusStartTime(Date.now());

      // Set timeout to hide stimulus if user doesn't respond
      stimulusTimerRef.current = setTimeout(() => {
        if (isActive && gameActive.current) {
          // User didn't respond in time
          setIsActive(false);
          setMissedStimuli((prev) => prev + 1);
          setScore((prev) => Math.max(0, prev - 10));

          setShowFeedback(true);
          setFeedbackMessage("Too slow!");
          setTimeout(() => setShowFeedback(false), 800);

          // Move to next round
          if (roundsCompleted < totalRounds - 1) {
            setRoundsCompleted((prev) => prev + 1);
            startNewRound();
          } else {
            endGame();
          }
        }
      }, settings.stimulusDuration);
    }, delay);
  };

  // Handle user response
  const handleReaction = () => {
    // If stimulus is not active, this is a false start
    if (!isActive) {
      setFalseStarts((prev) => prev + 1);
      setScore((prev) => Math.max(0, prev - 25));

      setShowFeedback(true);
      setFeedbackMessage("False start!");
      setTimeout(() => setShowFeedback(false), 800);

      // Clear the current timeout and start a new round
      clearTimeout(timeoutRef.current);
      startNewRound();
      return;
    }

    // If stimulus is active, record the reaction time
    setStimulusEndTime(Date.now());
    const reactionTime = Date.now() - stimulusStartTime;

    // Clear the stimulus timeout
    clearTimeout(stimulusTimerRef.current);

    // Update stats
    setReactionTimes((prev) => [...prev, reactionTime]);
    setCorrectResponses((prev) => prev + 1);

    // Calculate score based on reaction time
    // Faster reactions = higher scores
    let roundScore;
    if (reactionTime < 200) {
      roundScore = 50; // Excellent (was 30)
      setFeedbackMessage("Excellent!");
    } else if (reactionTime < 300) {
      roundScore = 35; // Great (was 25)
      setFeedbackMessage("Great!");
    } else if (reactionTime < 400) {
      roundScore = 20; // Good (same)
      setFeedbackMessage("Good!");
    } else if (reactionTime < 500) {
      roundScore = 10; // Okay (was 15)
      setFeedbackMessage("Okay");
    } else {
      roundScore = 5; // Slow (was 10)
      setFeedbackMessage("Slow");
    }
    setScore((prev) => prev + roundScore);

    // Show feedback
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 800);

    // Reset the active state
    setIsActive(false);

    // Move to next round or end game
    if (roundsCompleted < totalRounds - 1) {
      setRoundsCompleted((prev) => prev + 1);
      startNewRound();
    } else {
      endGame();
    }
  };

  // Handle the tutorial steps
  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);

      // If moving to step 1, show a practice stimulus
      if (tutorialStep === 0) {
        setTimeout(() => {
          setTutorialActive(true);
          // Auto-hide after 2 seconds if they don't click
          setTimeout(() => {
            setTutorialActive(false);
          }, 2000);
        }, 1000);
      }
    } else {
      // End tutorial and start the game
      setGameMode("play");
      startGame();
    }
  };

  // Save score to Firebase
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Resolve userName with fallbacks
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "reaction-time",
        gameId: "reaction-time",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        correctResponses,
        incorrectResponses,
        falseStarts,
        missedStimuli,
        avgReactionTime,
        bestReactionTime,
        worstReactionTime,
        roundsCompleted,
        gameLength: gameEndTime - gameStartTime,
        timestamp: new Date(),
      };

      // 🔗 Firebase references
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "reaction-time"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Sorting: score > faster average reaction time
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        return a.avgReactionTime - b.avgReactionTime;
      };

      // 🔁 Transaction to update leaderboard and user doc
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const difficultyKey = difficulty.toString();
        let currentLeaderboard =
          leaderboardData.leaderboards[difficultyKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[difficultyKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 💾 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset game state
      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  // Get shape classes based on the shape name
  const getShapeClasses = (shape) => {
    switch (shape) {
      case "circle":
        return "rounded-full";
      case "square":
        return "rounded-none";
      case "triangle":
        return "triangle";
      case "diamond":
        return "diamond";
      case "hexagon":
        return "hexagon";
      default:
        return "rounded-full";
    }
  };

  // Get position classes based on position name
  const getPositionClasses = (position) => {
    switch (position) {
      case "top":
        return "top-5 left-1/2 transform -translate-x-1/2";
      case "right":
        return "top-1/2 right-5 transform -translate-y-1/2";
      case "bottom":
        return "bottom-5 left-1/2 transform -translate-x-1/2";
      case "left":
        return "top-1/2 left-5 transform -translate-y-1/2";
      case "center":
        return "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2";
      default:
        return "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2";
    }
  };

  // Render stimulus based on type and value
  const renderStimulus = () => {
    if (!currentStimulus) return null;

    if (stimulusType === "color") {
      return (
        <div
          className={`w-24 h-24 ${currentStimulus} rounded-full shadow-lg`}
        ></div>
      );
    } else if (stimulusType === "shape") {
      return (
        <div
          className={`w-24 h-24 ${getShapeClasses(
            currentStimulus
          )} bg-orange-500 shadow-lg`}
        ></div>
      );
    } else if (stimulusType === "position") {
      return (
        <div
          className={`absolute ${getPositionClasses(
            currentStimulus
          )} w-24 h-24 bg-orange-500 rounded-full shadow-lg`}
        ></div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-orange-300">
              Speed Reactor
            </h1>
            <div className="p-2 bg-orange-500/10 rounded-full">
              <Zap className="text-orange-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-500/10 rounded-lg">
                      <Zap className="text-orange-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Speed Reactor Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h3 className="text-orange-300 font-medium mb-2">
                      Game Overview
                    </h3>
                    <p className="text-slate-300">
                      Speed Reactor is a reaction time test that measures and
                      improves your processing speed. You'll respond as quickly
                      as possible to visual stimuli that appear after random
                      intervals.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-orange-300 font-medium mb-2">
                      How to Play
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-slate-300">
                      <li>
                        Watch the reaction area and wait for a stimulus to
                        appear
                      </li>
                      <li>
                        Click/tap as quickly as possible when you see the
                        stimulus
                      </li>
                      <li>
                        Avoid "false starts" by waiting for the stimulus to
                        appear before clicking
                      </li>
                      <li>
                        Your score is based on reaction speed and accuracy
                      </li>
                      <li>Complete all rounds to see your results</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-orange-300 font-medium mb-2">
                      Difficulty Levels
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        <span className="font-medium">Easy:</span> Longer visual
                        cues, consistent stimuli
                      </li>
                      <li>
                        <span className="font-medium">Standard:</span> Moderate
                        timing, variable stimuli
                      </li>
                      <li>
                        <span className="font-medium">Advanced:</span> Shorter
                        timing, distractors present
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-orange-300 font-medium mb-2">
                      Recovery Benefits
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        Targets processing speed often impaired during recovery
                      </li>
                      <li>
                        Strengthens neural pathways for rapid information
                        processing
                      </li>
                      <li>Improves attention and response inhibition</li>
                      <li>
                        May help reduce reaction time in everyday activities
                      </li>
                    </ul>
                  </div>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-red-700 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Zap size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Speed Reactor
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Test and improve your reaction time with this processing speed
                  challenge. React quickly to visual stimuli to strengthen
                  neural pathways.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-orange-500/10 rounded-full mb-2">
                    <Zap size={24} className="text-orange-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Processing Speed
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Improves neural response time and information processing
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-orange-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-orange-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">3-5 minutes</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Quick sessions for regular training
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-orange-500/10 rounded-full mb-2">
                    <Activity size={24} className="text-orange-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Response Metrics
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Track improvements in reaction time
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {["easy", "standard", "advanced"].map((level) => (
                      <button
                        key={level}
                        onClick={() => setDifficulty(level)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors capitalize ${
                          difficulty === level
                            ? "bg-gradient-to-r from-orange-500 to-red-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => {
                      setGameMode("play");
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Avg. Reaction
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((score, index) => {
                          // Format the name to show first name and first letter of last name
                          let formattedName = score.userName;

                          if (score.userName && score.userName.includes(" ")) {
                            const nameParts = score.userName.split(" ");
                            const firstName = nameParts[0];
                            const lastInitial = nameParts[1]
                              ? nameParts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }

                          return (
                            <tr
                              key={score.id}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300 capitalize">
                                {score.difficulty}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {score.avgReactionTime
                                  ? formatMs(score.avgReactionTime)
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-right text-orange-300 font-medium">
                                {score.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Speed Reactor
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Speed Reactor measures how quickly you can respond to
                        visual stimuli. Your goal is to click or tap as soon as
                        you see something appear on the screen.
                      </p>
                      <p className="text-slate-300">
                        Let's practice with a simple example. I'll show you what
                        to do.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Great! In this game, you'll have to wait for the
                        stimulus to appear.
                      </p>
                      <p className="text-slate-300">
                        <span className="text-red-400 font-medium">
                          Important:
                        </span>{" "}
                        Only click when you see the stimulus. Clicking too early
                        counts as a "false start" and reduces your score.
                      </p>
                      <p className="text-slate-300">
                        The faster you react, the higher your score will be!
                      </p>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        In the {difficulty} difficulty mode, you'll see:
                      </p>

                      {difficulty === "easy" && (
                        <ul className="list-disc list-inside space-y-2 text-slate-300">
                          <li>Colored circles appearing after random delays</li>
                          <li>You'll have up to 2 seconds to respond</li>
                          <li>
                            The timing between stimuli will be 2-5 seconds
                          </li>
                        </ul>
                      )}

                      {difficulty === "standard" && (
                        <ul className="list-disc list-inside space-y-2 text-slate-300">
                          <li>
                            Different colored shapes appearing after random
                            delays
                          </li>
                          <li>The shapes may appear in different positions</li>
                          <li>You'll have up to 1.5 seconds to respond</li>
                          <li>
                            The timing between stimuli will be 1.5-4 seconds
                          </li>
                        </ul>
                      )}

                      {difficulty === "advanced" && (
                        <ul className="list-disc list-inside space-y-2 text-slate-300">
                          <li>
                            Various stimuli types with shorter response windows
                          </li>
                          <li>
                            Occasional distractor elements to test your focus
                          </li>
                          <li>You'll have only 1 second to respond</li>
                          <li>
                            The timing between stimuli will be 1-3 seconds
                          </li>
                        </ul>
                      )}
                    </div>
                  )}

                  {tutorialStep === 3 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        You've got it! Here's a quick summary:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-slate-300">
                        <li>Wait for a stimulus to appear on screen</li>
                        <li>Click/tap as quickly as you can when you see it</li>
                        <li>Avoid clicking too early (false starts)</li>
                        <li>Try to beat your best reaction time</li>
                        <li>Complete all rounds to see your results</li>
                      </ul>
                      <p className="text-slate-300">
                        Ready to test your reaction time? Let's go!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tutorial visualization */}
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="flex-1 flex justify-center">
                  <div
                    className={`w-80 h-80 bg-slate-900/60 rounded-xl border border-slate-700 flex items-center justify-center relative ${
                      tutorialStep === 1 ? "cursor-pointer" : ""
                    }`}
                    onClick={() => {
                      if (tutorialActive) {
                        setTutorialActive(false);
                        advanceTutorial();
                      }
                    }}
                  >
                    {tutorialStep === 0 && (
                      <div className="text-slate-400 text-center p-4">
                        <Crosshair
                          size={40}
                          className="mx-auto mb-3 text-slate-500"
                        />
                        <p>The stimulus will appear here</p>
                      </div>
                    )}

                    {tutorialStep === 1 && !tutorialActive && (
                      <div className="text-slate-400 text-center p-4">
                        <AlertCircle
                          size={30}
                          className="mx-auto mb-3 text-orange-400"
                        />
                        <p>Wait for it...</p>
                      </div>
                    )}

                    {tutorialStep === 1 && tutorialActive && (
                      <div className="w-24 h-24 bg-orange-500 rounded-full animate-pulse"></div>
                    )}

                    {tutorialStep > 1 && (
                      <div className="text-slate-400 text-center p-4">
                        <Target
                          size={40}
                          className="mx-auto mb-3 text-orange-400"
                        />
                        <p>Be ready to click when you see the stimulus</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 space-y-4">
                  {tutorialStep === 1 && (
                    <div className="bg-slate-700/30 p-4 rounded-lg">
                      <h3 className="text-white font-medium mb-2">Practice:</h3>
                      <p className="text-slate-300">
                        {tutorialActive
                          ? "Click/tap the orange circle now!"
                          : "Wait for the orange circle to appear..."}
                      </p>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  <div className="flex justify-between">
                    {tutorialStep > 0 ? (
                      <button
                        onClick={() => setTutorialStep((prev) => prev - 1)}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Previous
                      </button>
                    ) : (
                      <button
                        onClick={() => setGameMode("intro")}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Back
                      </button>
                    )}

                    <button
                      onClick={advanceTutorial}
                      className="py-2 px-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-lg transition-colors"
                    >
                      {tutorialStep < 3 ? "Next" : "Start Game"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Game Play Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-orange-500/20 to-red-600/20 px-4 py-2 rounded-lg">
                  <span className="text-orange-300 font-medium capitalize">
                    Level: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-orange-500/20 to-red-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Game reaction area */}
                <div className="relative w-full max-w-lg">
                  <div
                    className={`w-full aspect-square bg-slate-900/60 rounded-xl border border-slate-700 flex items-center justify-center relative overflow-hidden ${
                      isPlaying ? "cursor-pointer" : "cursor-default"
                    }`}
                    onClick={() => {
                      if (isPlaying) {
                        handleReaction();
                      }
                    }}
                    ref={reactionAreaRef}
                  >
                    {isPlaying && !isActive && !showFeedback && (
                      <div className="text-slate-400 text-center p-4">
                        <div className="mb-3">
                          <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mx-auto">
                            <AlertCircle size={24} className="text-slate-500" />
                          </div>
                        </div>
                        <p className="text-slate-500">Wait for stimulus...</p>
                      </div>
                    )}

                    {isActive && renderStimulus()}

                    {/* Feedback overlay */}
                    <AnimatePresence>
                      {showFeedback && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.2 }}
                          className="absolute inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
                        >
                          <div className="text-center">
                            <div
                              className={`text-3xl font-bold ${
                                feedbackMessage.includes("False")
                                  ? "text-red-400"
                                  : feedbackMessage.includes("Too slow")
                                  ? "text-yellow-400"
                                  : "text-green-400"
                              }`}
                            >
                              {feedbackMessage}
                            </div>
                            {feedbackMessage === "False start!" && (
                              <p className="text-slate-400 mt-2">
                                Wait for the stimulus
                              </p>
                            )}
                            {feedbackMessage === "Too slow!" && (
                              <p className="text-slate-400 mt-2">
                                Try to react faster
                              </p>
                            )}
                            {!feedbackMessage.includes("False") &&
                              !feedbackMessage.includes("Too slow") &&
                              reactionTimes.length > 0 && (
                                <p className="text-slate-300 mt-2">
                                  {formatMs(
                                    reactionTimes[reactionTimes.length - 1]
                                  )}
                                </p>
                              )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Game stats */}
                <div className="md:w-1/3 space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">
                      Game Progress
                    </h3>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400 text-sm">Round</span>
                      <span className="text-white font-medium">
                        {roundsCompleted + 1}/{totalRounds}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-red-600"
                        style={{
                          width: `${(roundsCompleted / totalRounds) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">
                      Current Stats
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">Best Reaction</span>
                        <span className="text-green-400 font-medium">
                          {reactionTimes.length > 0
                            ? formatMs(Math.min(...reactionTimes))
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">Latest Reaction</span>
                        <span className="text-orange-300 font-medium">
                          {reactionTimes.length > 0
                            ? formatMs(reactionTimes[reactionTimes.length - 1])
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">
                          Correct Responses
                        </span>
                        <span className="text-white">{correctResponses}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">False Starts</span>
                        <span className="text-red-400">{falseStarts}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">Missed Stimuli</span>
                        <span className="text-yellow-400">{missedStimuli}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            "Are you sure you want to end the game early?"
                          )
                        ) {
                          endGame();
                        }
                      }}
                      className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You've completed the Speed Reactor challenge on {difficulty}{" "}
                  difficulty.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-orange-400" />
                    Your Results
                  </h3>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-orange-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium capitalize">
                        {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct Responses</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">False Starts</span>
                      <span className="text-red-400">{falseStarts}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Missed Stimuli</span>
                      <span className="text-yellow-400">{missedStimuli}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Reaction Time</span>
                      <span className="text-white">
                        {avgReactionTime > 0
                          ? formatMs(avgReactionTime)
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Best Reaction Time</span>
                      <span className="text-green-400">
                        {bestReactionTime > 0
                          ? formatMs(bestReactionTime)
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-orange-400" />
                    Performance Analysis
                  </h3>

                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">
                          Response Rate
                        </span>
                        <span className="text-white text-sm">
                          {totalRounds > 0
                            ? `${Math.round(
                                (correctResponses / totalRounds) * 100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-500 to-red-600"
                          style={{
                            width:
                              totalRounds > 0
                                ? `${(correctResponses / totalRounds) * 100}%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Reaction Time Classification
                      </h4>
                      <div className="bg-slate-700/30 p-3 rounded-lg text-sm">
                        {avgReactionTime > 0 && (
                          <>
                            {avgReactionTime < 200 && (
                              <p className="text-green-400">
                                Elite (Under 200ms): Exceptional reaction time!
                              </p>
                            )}
                            {avgReactionTime >= 200 &&
                              avgReactionTime < 300 && (
                                <p className="text-green-300">
                                  Excellent (200-300ms): Very fast reactions!
                                </p>
                              )}
                            {avgReactionTime >= 300 &&
                              avgReactionTime < 400 && (
                                <p className="text-green-200">
                                  Good (300-400ms): Above average speed.
                                </p>
                              )}
                            {avgReactionTime >= 400 &&
                              avgReactionTime < 500 && (
                                <p className="text-yellow-300">
                                  Average (400-500ms): Normal reaction time.
                                </p>
                              )}
                            {avgReactionTime >= 500 && (
                              <p className="text-orange-300">
                                Developing (Over 500ms): Room for improvement.
                              </p>
                            )}
                          </>
                        )}
                        {avgReactionTime === 0 && (
                          <p className="text-slate-400">
                            Complete more rounds to see your classification.
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-orange-500/10 rounded-full mt-0.5">
                            <Zap size={14} className="text-orange-400" />
                          </div>
                          <span className="text-slate-300">
                            Improved processing speed in neural pathways
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-orange-500/10 rounded-full mt-0.5">
                            <Zap size={14} className="text-orange-400" />
                          </div>
                          <span className="text-slate-300">
                            Enhanced attention and response control
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-orange-500/10 rounded-full mt-0.5">
                            <Zap size={14} className="text-orange-400" />
                          </div>
                          <span className="text-slate-300">
                            Strengthened hand-eye coordination
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameWasCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeedReactor;
